<template>
  <header id="navigation" class="py-2">
    <div class="headerWrap px-3 px-md-0">
      <div class="row justify-content-between align-items-center">
        <div class="col-auto d-flex align-items-center header__logo">
          <StoreLink
            class="d-flex align-items-center header__logo__link"
            href="/"
          >
            <img
              alt="PCLogo"
              width="60px"
              height="60px"
              class="phoenix-logo"
              src="@/assets/img/logos/PCLogoHeader.png"
            />
            <div class="row">
              <div class="col-12">
                <img
                  alt="PLCnextStore"
                  class="phoenix-logo mx-3"
                  height="20px"
                  src="@/assets/img/logos/PLCnextStore.svg"
                /><br />
                <h5 class="h5-white float-end" style="margin-bottom: 0px">
                  {{ storeHubText }}
                </h5>
              </div>
            </div>
          </StoreLink>
        </div>

        <div class="col-auto d-flex align-items-center">
          <!-- Burger (mobile only) -->
          <button
            class="burgerBtn d-md-none"
            type="button"
            :aria-expanded="mobileMenuOpen ? 'true' : 'false'"
            aria-controls="mobileNav"
            aria-label="Toggle navigation"
            @click="mobileMenuOpen = !mobileMenuOpen"
          >
            <span class="burgerLines" aria-hidden="true"></span>
          </button>

          <!-- Desktop nav (md+) -->
          <BNav
            class="d-none d-md-flex justify-content-center justify-content-md-end align-items-center header__nav"
          >
            <HeaderNavItems
              variant="desktop"
              :logged-in="loggedIn"
              :approved-contributor="approvedContributor"
              :show-developer-profile="showDeveloperProfile"
              :auth-user-uuid="authUser?.uuid"
              :auth-user-name="authUser?.userName"
              :auth-user-company-id="authUser?.companyId"
              :auth-user-is-admin="authUserIsAdmin"
              :user-registration-status-includes-user-data="
                userRegistrationStatus.includes('USER_DATA')
              "
              :user-registration-status-includes-company-data="
                userRegistrationStatus.includes('COMPANY_DATA')
              "
              :is-profile-tab-active="isProfileTabActive"
              :open-login-modal="openLoginModal"
              :logout="logout"
              @navigate="closeMobileMenu"
            />
          </BNav>
        </div>
      </div>

      <!-- Mobile collapse (sm only) -->
      <BCollapse
        id="mobileNav"
        v-model="mobileMenuOpen"
        class="d-md-none mobileNav"
      >
        <BNav class="flex-column">
          <HeaderNavItems
            variant="mobile"
            :logged-in="loggedIn"
            :approved-contributor="approvedContributor"
            :show-developer-profile="showDeveloperProfile"
            :auth-user-uuid="authUser?.uuid"
            :auth-user-name="authUser?.userName"
            :auth-user-company-id="authUser?.companyId"
            :auth-user-is-admin="authUserIsAdmin"
            :user-registration-status-includes-user-data="
              userRegistrationStatus.includes('USER_DATA')
            "
            :user-registration-status-includes-company-data="
              userRegistrationStatus.includes('COMPANY_DATA')
            "
            :is-profile-tab-active="isProfileTabActive"
            :open-login-modal="openLoginModal"
            :logout="logout"
            @navigate="closeMobileMenu"
          />
        </BNav>
      </BCollapse>
    </div>

    <LoginModal />
    <RegistrationNotCompleteModal />
    <UserGotCompanyInviteModal />
  </header>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useRoute } from "#app";
import { useUserStore } from "~/stores/userstore";
import { useUiStore } from "~/stores/ui/uistore";
import { BNav, BCollapse } from "bootstrap-vue-next";
import LoginModal from "~/components/shared/modals/LoginModal.vue";
import { useCompanyStore } from "~/stores/companystore";
import HeaderNavItems from "~/components/shared/navigation/HeaderNavItems.vue";
import RegistrationNotCompleteModal from "~/components/shared/modals/RegistrationNotCompleteModal.vue";
import StoreLink from "~/components/shared/buttons/StoreLink.vue";
import UserGotCompanyInviteModal from "~/components/shared/modals/UserGotCompanyInviteModal.vue";

const route = useRoute();
const userStore = useUserStore();
const companyStore = useCompanyStore();
const uiStore = useUiStore();

const mobileMenuOpen = ref(false);
function closeMobileMenu(): void {
  mobileMenuOpen.value = false;
}
watch(
  () => route.fullPath,
  () => closeMobileMenu(),
);

const loggedIn = computed(() => userStore.isLoggedIn);
const userRegistrationStatus = computed(() => userStore.userRegistrationStatus);
const authUser = computed(() => userStore.authUser);
const storeHubText = computed(() => {
  switch (route.params.hub) {
    case "eu":
      return "Europe";
    case "us":
      return "United States";
    case "world":
      return "World";
    default:
      return "";
  }
});

const authUserIsAdmin = computed(() => {
  return userStore.authUserRoles?.some((role) => role === "ROLE_ADMIN");
});

const approvedContributor = computed(() => {
  return !!companyStore.userCompany?.approvedContributor;
});

const showDeveloperProfile = computed(() => {
  if (!userStore.isCompanyAdmin) {
    return false;
  }
  if (!userStore.authUser?.companyId) {
    return false;
  }
  // just using url as one property which determines that the company profiles has already been saved once
  if (
    companyStore.userCompany &&
    companyStore.userCompany.companyProfile &&
    (companyStore.userCompany.companyProfile.websiteUrl ||
      uiStore.userClickedBecomeContributor)
  ) {
    return true;
  }
  return false;
});

function openLoginModal(): void {
  uiStore.showLoginModal = true;
}

function logout(): void {
  userStore.logout();
}

/**
 * Checks whether a given profile tab is active.
 */
function isProfileTabActive(tabName: string): boolean {
  if (authUser.value && route.params.user === authUser.value?.uuid) {
    return route.path === `/users/${authUser.value?.uuid}/${tabName}`;
  }
  return false;
}
</script>

<style lang="scss" scoped>
@use "@/sass/components/Navigation.scss" as *;
</style>
